import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box, Button, Card, Checkbox, FormControl, FormLabel, Input,
  Radio, RadioGroup, Stack, Typography, Select, Option,
  IconButton
} from '@mui/joy';
import { useFeedback } from './feedback/Service';

export const hasInvalidScreeningQuestions = (questions = []) =>
  questions.some(q => !q.text || (q.type === 'numeric' && ((!q.minValue && q.minValue !== 0) || (!q.maxValue && q.maxValue !== 0))));

// Form Constructor Component
export const FormConstructor = ({ questions = [], setQuestions, onClose, embedded = false }) => {
  // const [formTitle, setFormTitle] = useState('');
  // const [questions, setQuestions] = useState([]);
  const feedback = useFeedback();

  const handleSave = () => {
    // Check required fields
    if (hasInvalidScreeningQuestions(questions)) {
      feedback.snackbar({ text: "Please fill in all required fields for each question.", type: "error" });
      return;
    }
    onClose?.();
  }
  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: Date.now(),
        text: "",
        type: 'yesno',
        correctAnswer: 'yes',
        minValue: 0,
        maxValue: 10,
        cutting: false
      }
    ]);
  };

  const updateQuestion = (id, field, value) => {
    setQuestions(questions.map(q =>
      q.id === id ? { ...q, [field]: value } : q
    ));
  };

  const removeQuestion = (id) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  return (
    <Box sx={{ p: embedded ? 0 : 2, mx: 'auto', width: '100%', pt: 0 }} maxWidth={embedded ? undefined : "md"}>
      <Stack
        direction="row"
        alignItems="center"
        mb={0}
        sx={embedded ? { mb: 2 } : { position: "sticky", top: 0, py: 2, backgroundColor: 'background.body', zIndex: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {!embedded && <Typography level="h4" mb={0}>Screening Questions</Typography>}
        <Button
          startDecorator={<AddIcon />}
          onClick={addQuestion}
          sx={{ mt: 0, float: 'right', ml: embedded ? 0 : 'auto', mr: embedded ? 0 : 2 }}
          variant="soft"
          color="primary"
        >
          Add Question
        </Button>
        {!embedded && (
          <Button
            variant="solid"
            color="primary"
            onClick={handleSave}
            sx={{ minWidth: 100 }}
          >
            Save & Close
          </Button>
        )}
      </Stack>
      {!embedded && (
        <Typography level="body-md" mb={3} mt={2}>
          Add questions for candidates to answer before the interview. This is optional and can help you gather more information or filter applications.
        </Typography>
      )}
      {/* <FormControl sx={{ mb: 2 }}>
        <FormLabel>Form Title</FormLabel>
        <Input
          value={formTitle}
          onChange={(e) => setFormTitle(e.target.value)}
          placeholder="Enter form title"
        />
      </FormControl> */}

      {questions.map((question, index) => (
        <Card key={question.id} sx={{ mb: 2, p: 2, width: "100%" }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0 }}>
            <Typography level="title-md">Question {index + 1}</Typography>
            <IconButton
              variant="plain"
              color="danger"
              onClick={() => removeQuestion(question.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          <FormControl sx={{ mb: 1 }} required>
            <FormLabel>Question Text</FormLabel>
            <Input
              value={question.text}
              onChange={(e) => updateQuestion(question.id, 'text', e.target.value)}
              placeholder="Enter question"
            />
          </FormControl>

          <FormControl sx={{ mb: 1 }}>
            <Checkbox size="md" label="Required to proceed to the video interview" checked={question.cutting} onChange={(e) => updateQuestion(question.id, 'cutting', e.target.checked)} />
          </FormControl>

          <FormControl sx={{ mb: 1 }}>
            <FormLabel>Question Type</FormLabel>
            <Select
              value={question.type}
              onChange={(_, value) => updateQuestion(question.id, 'type', value)}
            >
              <Option value="yesno">Yes/No</Option>
              <Option value="numeric">Numeric</Option>
            </Select>
          </FormControl>

          {question.type === 'yesno' ? (
            <FormControl>
              <FormLabel>Correct Answer</FormLabel>
              <RadioGroup
                value={question.correctAnswer}
                onChange={(e) => updateQuestion(question.id, 'correctAnswer', e.target.value)}
              >
                <Radio value="yes" label="Yes" />
                <Radio value="no" label="No" />
              </RadioGroup>
            </FormControl>
          ) : (
            <Stack direction="row" spacing={2}>
              <FormControl required>
                <FormLabel>Min Correct Value</FormLabel>
                <Input
                  type="number"
                  value={question.minValue}
                  onChange={(e) => updateQuestion(question.id, 'minValue', Number(e.target.value))}
                  // select all text on focus
                  onFocus={(e) => e.target.select()}
                />
              </FormControl>
              <FormControl required>
                <FormLabel>Max Correct Value</FormLabel>
                <Input
                  type="number"
                  value={question.maxValue}
                  onChange={(e) => updateQuestion(question.id, 'maxValue', Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                />
              </FormControl>
            </Stack>
          )}
        </Card>
      ))}

      {embedded && questions.length === 0 && (
        <Card variant="soft" color="neutral" sx={{ p: 3, textAlign: 'center' }}>
          <Typography level="title-sm">No screening questions</Typography>
          <Typography level="body-sm" color="neutral">
            Candidates will go directly to the interview after applying.
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default FormConstructor;
